import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Eye, FilePenLine, Trash2, Filter, AArrowUp, AArrowDown } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, DropdownMenuCheckboxItem } from "../../components/ui/dropdown-menu";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import CatalogItemDialogForm from "../../components/shared/CatalogItemDialogForm";
import { formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import { createInstoreFlips, listAllInstoreFlips, updateInstoreFlips, updateInstoreFlipsStatus } from "../../reducers/instore.slice";
import { AuthContext } from "../../context/authContext";
import useMediaQuery from "../../components/shared/useMediaQuery";

const ISF_STATUSES = ["ACTIVE", "INACTIVE"];

const InstoreFlipsPage = () => {
  const [openDeleteInstore, setOpenDeleteInstore] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [detailRow, setDetailRow] = useState(null);
  const [imgPreviewOpen, setImgPreviewOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [nameExpanded, setNameExpanded] = useState(false);
  const [instoreDetails, setInstoreDetails] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const stored = sessionStorage.getItem("instoreFlips_selectedStatuses");
    return stored ? JSON.parse(stored) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("instoreFlips_sortDirection") || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [createOpen, setCreateOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const getAllInstoreFlips = () => {
    setShowLoader(true);
    dispatch(listAllInstoreFlips()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) { setShowLoader(false); showToast(res?.error?.message, "error"); return; }
      const formatted = res?.payload?.map((item) => ({
        ...item,
        buyPrice: formatNumberToDollar(item?.buy_price),
        cashoutPrice: formatNumberToDollar(item?.cashout_price),
      }));
      setShowLoader(false);
      setInstoreDetails(formatted);
    });
  };

  useEffect(() => { getAllInstoreFlips(); }, [isRefresh]);

  const filterData = () => {
    let filtered = [...instoreDetails];
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.upc && String(item.upc).toLowerCase().includes(t)) ||
          (item.retailer_name && item.retailer_name.toLowerCase().includes(t)) ||
          (item.item_name && item.item_name.toLowerCase().includes(t))
      );
    }
    if (selectedStatuses.length > 0) filtered = filtered.filter((item) => selectedStatuses.includes(item?.status));
    if (sortDirection === "asc") filtered.sort((a, b) => Number(a?.total_quantity || 0) - Number(b?.total_quantity || 0));
    else if (sortDirection === "desc") filtered.sort((a, b) => Number(b?.total_quantity || 0) - Number(a?.total_quantity || 0));
    setFilteredData(filtered);
    setCurrentPage(0);
  };

  useEffect(() => { filterData(); }, [instoreDetails, selectedStatuses, sortDirection, searchTerm]);

  useEffect(() => {
    sessionStorage.setItem("instoreFlips_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) sessionStorage.setItem("instoreFlips_sortDirection", sortDirection);
    else sessionStorage.removeItem("instoreFlips_sortDirection");
  }, [sortDirection]);

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const handlePressDelete = (row) => { setSelectedRow(row); setOpenDeleteInstore(true); };

  const handleDeleteInstore = () => {
    setShowLoader(true);
    dispatch(updateInstoreFlipsStatus({ id: selectedRow.id, status: "INACTIVE" })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) { setShowLoader(false); showToast(res?.error?.message, "error"); return; }
      setIsRefresh(!isRefresh);
      setShowLoader(false);
      setOpenDeleteInstore(false);
    });
  };

  const isfStats = useMemo(() => {
    const active = instoreDetails.filter((d) => d.status === "ACTIVE").length;
    const inactive = instoreDetails.filter((d) => d.status === "INACTIVE").length;
    return { active, inactive, total: instoreDetails.length };
  }, [instoreDetails]);

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);
  const paginatedRows = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const columns = [
    {
      key: "item_name",
      label: "Item Name",
      width: 210,
      maxWidth: 210,
      render: (row) => <span style={{ color: "#fff", fontWeight: 500 }}>{row?.item_name}</span>,
    },
    { key: "upc", label: "UPC", width: 130 },
    { key: "retailer_name", label: "Retailer Name", width: 140 },
    { key: "total_quantity", label: "Qty", width: 64, align: "center" },
    { key: "buyPrice", label: "Buy Price", width: 90, align: "right" },
    { key: "cashoutPrice", label: "Cashout Price", width: 110, align: "right" },
    {
      key: "status",
      label: "Status",
      width: 96,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "action",
      label: "Action",
      width: 110,
      align: "center",
      render: (row) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}>
          <Button
            variant="ghost"
            size="icon-sm"
            title="View"
            onClick={(e) => { e.stopPropagation(); setImgError(false); setNameExpanded(false); setDetailRow(row); }}
            style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <Eye size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Edit"
            onClick={(e) => { e.stopPropagation(); setEditData(row); }}
            style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <FilePenLine size={15} />
          </Button>
          {row?.status === "ACTIVE" && (
            <Button
              variant="ghost"
              size="icon-sm"
              title="Delete"
              onClick={(e) => { e.stopPropagation(); handlePressDelete(row); }}
              style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(242,112,89,0.08)", border: "1px solid rgba(242,112,89,0.2)" }}
            >
              <Trash2 size={15} style={{ color: "#f27059" }} />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
        <div style={{ marginBottom: 20 }}>
          <PageHeader
            title="Instore Flips"
            subtitle="Manage in-store flip inventory with buy and cashout pricing."
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(3, minmax(0, 1fr))" : "repeat(auto-fit, minmax(180px, 1fr))",
            gap: isMobile ? 8 : 16,
            marginBottom: 20,
          }}
        >
          <div style={statStyle("#4caf50", isMobile)}>
            <span style={statLabelStyle(isMobile)}>ACTIVE</span>
            <span style={statValueStyle(isMobile)}>{isfStats.active}</span>
          </div>
          <div style={statStyle("#f27059", isMobile)}>
            <span style={statLabelStyle(isMobile)}>INACTIVE</span>
            <span style={statValueStyle(isMobile)}>{isfStats.inactive}</span>
          </div>
          <div style={statStyle("#00ADB5", isMobile)}>
            <span style={statLabelStyle(isMobile)}>TOTAL</span>
            <span style={statValueStyle(isMobile)}>{isfStats.total}</span>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? 10 : 12,
              flexWrap: "wrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            {!isMobile && <RefreshButton onRefresh={getAllInstoreFlips} spinning={showLoader} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : undefined }}>
              <SearchBar
                placeholder="Search by UPC, retailer, name…"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
                  <Filter size={14} />
                  Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 4,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                {ISF_STATUSES.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={selectedStatuses.includes(s)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleStatusChange(s, checked)}
                  >
                    {s}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem active={sortDirection === "asc"} onClick={() => setSortDirection((prev) => prev === "asc" ? null : "asc")}>
                  <AArrowUp size={14} style={{ marginRight: 8 }} /> Sort Qty (Low to High)
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "desc"} onClick={() => setSortDirection((prev) => prev === "desc" ? null : "desc")}>
                  <AArrowDown size={14} style={{ marginRight: 8 }} /> Sort Qty (High to Low)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setCreateOpen(true)} style={{ width: isMobile ? "100%" : "auto" }}>
              Add Instore Flip
            </Button>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={paginatedRows}
          totalRows={filteredData.length}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={(n) => setCurrentPage(n)}
          onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
          loading={showLoader}
          minWidth={940}
        />
      </div>

      {/* Detail dialog */}
      <Dialog open={Boolean(detailRow)} onOpenChange={(open) => !open && setDetailRow(null)}>
        <DialogContent
          style={{
            maxWidth: 560,
            width: isMobile ? "calc(100vw - 16px)" : "min(560px, calc(100vw - 64px))",
            background: "#111",
            padding: isMobile ? 16 : 24,
          }}
        >
          <DialogHeader>
            <DialogTitle>Instore Flip Details</DialogTitle>
            <DialogDescription>View details for this instore flip item.</DialogDescription>
          </DialogHeader>
          {detailRow && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 16,
                  gap: 12,
                }}
              >
                <p
                  onClick={() => setNameExpanded(!nameExpanded)}
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#fff",
                    margin: 0,
                    flex: 1,
                    cursor: "pointer",
                    display: "-webkit-box",
                    WebkitLineClamp: nameExpanded ? "unset" : 3,
                    WebkitBoxOrient: "vertical",
                    overflow: nameExpanded ? "visible" : "hidden",
                  }}
                >
                  {detailRow?.item_name}
                </p>
                {imgError || !detailRow?.item_image_url ? (
                  <div
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.1)",
                      flexShrink: 0,
                      background: "rgba(255,255,255,0.05)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#8b9296",
                      fontSize: 11,
                      textAlign: "center",
                      padding: 8,
                    }}
                  >
                    Image unavailable
                  </div>
                ) : (
                  <img
                    src={detailRow.item_image_url}
                    alt={detailRow.item_name}
                    onError={() => setImgError(true)}
                    onClick={() => setImgPreviewOpen(true)}
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.1)",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  />
                )}
              </div>

              {[
                { label: "UPC", value: detailRow?.upc },
                { label: "Retailer", value: detailRow?.retailer_name },
                { label: "Buy Price", value: detailRow?.buyPrice },
                { label: "Cashout Price", value: detailRow?.cashoutPrice },
                { label: "Total Quantity", value: detailRow?.total_quantity },
                { label: "Max Per User", value: detailRow?.max_per_user_quantity },
                { label: "Status", value: detailRow?.status ? <StatusBadge status={detailRow.status} /> : null },
                { label: "Tags", value: detailRow?.tags },
                { label: "Description", value: detailRow?.description },
                {
                  label: "Item Link",
                  value: detailRow?.item_link ? (
                    <a href={detailRow.item_link} target="_blank" rel="noopener noreferrer" style={{ color: "#00ADB5", textDecoration: "none", fontSize: 13 }}>
                      View item →
                    </a>
                  ) : null,
                },
              ]
                .filter((item) => item.value !== null && item.value !== undefined && item.value !== "")
                .map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      padding: "9px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: 10, color: "#8b9296", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 800 }}>{item.label}</span>
                    <span style={{ fontSize: 13, color: "#fff", textAlign: "right" }}>{item.value}</span>
                  </div>
                ))}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const row = detailRow;
                    setDetailRow(null);
                    setEditData(row);
                  }}
                >
                  <FilePenLine size={14} style={{ marginRight: 6 }} /> Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog open={imgPreviewOpen} onOpenChange={setImgPreviewOpen}>
        <DialogContent style={{ maxWidth: 480, background: "#111", padding: 16 }}>
          <DialogHeader>
            <DialogTitle>Item Image</DialogTitle>
            <DialogDescription>{detailRow?.item_name}</DialogDescription>
          </DialogHeader>
          <img
            src={detailRow?.item_image_url}
            alt={detailRow?.item_name}
            style={{ width: "100%", borderRadius: 8, objectFit: "contain", maxHeight: 400 }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={openDeleteInstore} onOpenChange={setOpenDeleteInstore}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Soft Delete</DialogTitle>
            <DialogDescription>
              You are about to delete this Instore Flip. Once done, the item will not be visible for members. Are you sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDeleteInstore(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteInstore}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={(open) => !open && setCreateOpen(false)}>
        <DialogContent style={{ maxWidth: 600, width: isMobile ? "calc(100vw - 16px)" : "90vw" }}>
          <DialogHeader>
            <DialogTitle>Add Instore Flip</DialogTitle>
            <DialogDescription>Create a new Instore Flip item with buy and cashout pricing.</DialogDescription>
          </DialogHeader>
          {createOpen && (
            <CatalogItemDialogForm
              actionCreator={createInstoreFlips}
              showToast={showToast}
              onSuccess={() => { setCreateOpen(false); getAllInstoreFlips(); }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editData)} onOpenChange={(open) => !open && setEditData(null)}>
        <DialogContent style={{ maxWidth: 600, width: isMobile ? "calc(100vw - 16px)" : "90vw" }}>
          <DialogHeader>
            <DialogTitle>Edit Instore Flip</DialogTitle>
            <DialogDescription>Update the Instore Flip item details below.</DialogDescription>
          </DialogHeader>
          {editData && (
            <CatalogItemDialogForm
              actionCreator={updateInstoreFlips}
              editData={editData}
              mode="edit"
              showToast={showToast}
              submitLabel="Update"
              submittingLabel="Updating..."
              onSuccess={() => { setEditData(null); getAllInstoreFlips(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const statStyle = (accent, isMobile) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: isMobile ? 10 : 14,
  padding: isMobile ? "10px 10px" : "16px 18px",
  minWidth: 0,
});

const statLabelStyle = (isMobile) => ({
  display: "block",
  color: "#c3c8cc",
  fontSize: isMobile ? 9 : 10.5,
  fontWeight: 800,
  letterSpacing: "0.08em",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const statValueStyle = (isMobile) => ({
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: isMobile ? 18 : 24,
  fontWeight: 800,
  marginTop: isMobile ? 4 : 8,
});

export default InstoreFlipsPage;
